import { useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { billingApi, saveBlob, type BillingEntity } from '@/lib/api/billing';
import { todayIso } from '@/lib/billing';

function monthStart(): string {
  const today = todayIso();
  return `${today.slice(0, 8)}01`;
}

/**
 * DATEV-Export (ADR-0003, Phase 7): Buchungen eines Zeitraums als EXTF-Buchungsstapel für
 * den Steuerberater. Sachkonten und Mandantennummer stehen beim Gläubiger.
 */
export default function DatevExportPage() {
  const [entities, setEntities] = useState<BillingEntity[]>([]);
  const [entityId, setEntityId] = useState('');
  const [from, setFrom] = useState(monthStart);
  const [to, setTo] = useState(todayIso);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    billingApi
      .listEntities()
      .then((response) => {
        const items = Array.isArray(response?.items) ? response.items : [];
        setEntities(items);
        const preferred = items.find((entity) => entity.isDefault) ?? items[0];
        if (preferred) setEntityId(String(preferred.id));
      })
      .catch((error) =>
        toast.error(error instanceof Error ? error.message : 'Gläubiger konnten nicht geladen werden'),
      );
  }, []);

  const entity = entities.find((item) => String(item.id) === entityId) ?? null;
  const missingClient = entity ? !entity.datevConsultantNumber || !entity.datevClientNumber : false;

  const download = async () => {
    setBusy(true);
    try {
      const file = await billingApi.downloadDatevExport({
        from,
        to,
        billingEntityId: entityId ? Number(entityId) : undefined,
      });
      saveBlob(file.blob, file.filename);
      toast.success('Buchungsstapel heruntergeladen');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Export fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold leading-7">DATEV-Export</h1>
        <p className="text-muted-foreground">
          Forderungen, Gutschriften, Gebühren und Zahlungen eines Zeitraums als
          EXTF-Buchungsstapel. Sidigo führt das Nebenbuch Debitoren; das Hauptbuch bleibt beim
          Steuerberater.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Zeitraum wählen</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="datev-entity">Gläubiger</Label>
              <Select value={entityId} onValueChange={setEntityId}>
                <SelectTrigger id="datev-entity">
                  <SelectValue placeholder="Auswählen" />
                </SelectTrigger>
                <SelectContent>
                  {entities.map((item) => (
                    <SelectItem key={item.id} value={String(item.id)}>
                      {item.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="datev-from">Von</Label>
              <Input
                id="datev-from"
                type="date"
                value={from}
                onChange={(event) => setFrom(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="datev-to">Bis</Label>
              <Input
                id="datev-to"
                type="date"
                value={to}
                onChange={(event) => setTo(event.target.value)}
              />
            </div>
          </div>

          {missingClient && (
            <p className="text-sm text-destructive">
              Für diesen Gläubiger fehlen Berater- und Mandantennummer. Sie stehen in der
              Administration unter Abrechnung → Belege &amp; Mahnwesen.
            </p>
          )}
          {entity && !missingClient && (
            <p className="text-sm text-muted-foreground">
              Kontenrahmen {entity.datevChart ?? 'SKR04'} · Erlöse{' '}
              {entity.datevRevenueAccount ?? 'Vorgabe'} · Gebühren{' '}
              {entity.datevFeeAccount ?? 'Vorgabe'} · Geldkonto{' '}
              {entity.datevBankAccount ?? 'Vorgabe'}
            </p>
          )}

          <Button onClick={() => void download()} disabled={busy || !entityId || missingClient}>
            <Download className="mr-2 h-4 w-4" />
            {busy ? 'Erstellt …' : 'Buchungsstapel herunterladen'}
          </Button>
          <p className="text-xs text-muted-foreground">
            Die Datei ist eine CSV in Windows-1252 mit EXTF-Kopf. Bitte einmal mit dem
            Steuerberater gegenprüfen, bevor sie regelmäßig eingespielt wird.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
